import { useEffect, useId, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@sw/api-client';
import { AUTH_ME_QUERY_KEY } from '@sw/auth';
import type { ChangePasswordRequest, ProfileResponse, UpdateMeRequest } from '@sw/auth';
import { tokens } from '../../theme/tokens';
import { useAuth } from '../../lib/auth';
import { api } from '../../lib/api-client';

/**
 * Profile pixel-perfect port del handoff `scouting-wizard-client-portal/Profile.html`
 * (Diego UX / Claude Design). SW-138.
 *
 * Estructura del handoff:
 *  - Breadcrumb: Opportunities / Profile
 *  - H1 "Profile" + lede
 *  - Section 01 · Account → Personal information
 *      - 2-col grid: First/Last name (read-only por ahora — PUT /auth/me TBD)
 *      - Email readout con lock icon + SSO badge del workspace
 *  - Section 02 · Notifications → Email notifications
 *      - Toggle "Opportunity updates" (disabled, BE pending)
 *      - Following list (empty state — feature pendiente)
 *
 * El edit del nombre no está wireado al backend (no existe PUT /api/client/auth/me en BE) —
 * follow-up SW-139. Mientras tanto los inputs muestran data real de useAuth() y el form
 * layout matchea el handoff exactamente.
 */
export default function ProfilePage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const queryClient = useQueryClient();

  const { initialFirstName, initialLastName, email, workspaceName } = useMemo(() => {
    const user = auth.user;
    const full = (user?.fullName ?? '').trim();
    const parts = full.split(/\s+/);
    return {
      initialFirstName: parts[0] ?? '',
      initialLastName: parts.slice(1).join(' '),
      email: user?.email ?? '',
      workspaceName: user?.workspaceName ?? '',
    };
  }, [auth.user]);

  // SW-139: form state editable. Inicializa con valores del auth.user; resync
  // cuando el /me se refetcha (post mutation invalidation).
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [phone, setPhone] = useState(auth.user?.phone ?? '');
  const [language, setLanguage] = useState(auth.user?.preferredLocale ?? 'es');
  const [notifyNewShortlist, setNotifyNewShortlist] = useState(auth.user?.notifyNewShortlist ?? true);
  const [notifyTalentUpdate, setNotifyTalentUpdate] = useState(auth.user?.notifyTalentUpdate ?? true);
  const [notifyWeeklyDigest, setNotifyWeeklyDigest] = useState(auth.user?.notifyWeeklyDigest ?? true);
  const [profileSavedAt, setProfileSavedAt] = useState<number | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);

  useEffect(() => {
    setFirstName(initialFirstName);
    setLastName(initialLastName);
    setPhone(auth.user?.phone ?? '');
    setLanguage(auth.user?.preferredLocale ?? 'es');
    setNotifyNewShortlist(auth.user?.notifyNewShortlist ?? true);
    setNotifyTalentUpdate(auth.user?.notifyTalentUpdate ?? true);
    setNotifyWeeklyDigest(auth.user?.notifyWeeklyDigest ?? true);
  }, [auth.user, initialFirstName, initialLastName]);

  const updateProfile = useMutation({
    mutationFn: async (body: UpdateMeRequest) =>
      api.put<ProfileResponse>('/api/client/auth/me', body),
    onSuccess: () => {
      setProfileSavedAt(Date.now());
      setProfileError(null);
      queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
    },
    onError: (err: unknown) => {
      const status = err instanceof ApiError ? err.status : 0;
      setProfileError(
        status === 400
          ? t('profile.errors.invalid')
          : t('profile.errors.network'),
      );
    },
  });

  function handleSubmitProfile(e: React.FormEvent) {
    e.preventDefault();
    setProfileError(null);
    setProfileSavedAt(null);
    // El BE acepta fullName completo (no split). Recomponemos.
    const composedName = `${firstName.trim()} ${lastName.trim()}`.trim();
    updateProfile.mutate({
      fullName: composedName,
      phone: phone.trim(),
      language,
      notifyNewShortlist,
      notifyTalentUpdate,
      notifyWeeklyDigest,
    });
  }

  return (
    <Box
      data-testid="profile-page"
      sx={{ maxWidth: 760, mx: 'auto', width: '100%', fontFamily: tokens.font.display }}
    >
      <Box
        component="nav"
        aria-label="Breadcrumb"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          py: '4px',
          pb: '18px',
          color: tokens.color.muted,
          fontSize: 13,
          borderBottom: `1px solid ${tokens.color.line}`,
          mb: '28px',
        }}
      >
        <Box
          component={RouterLink}
          to="/opportunities"
          sx={{
            color: tokens.color.muted,
            textDecoration: 'none',
            padding: '4px 8px',
            borderRadius: '6px',
            transition: 'color .15s, background .15s',
            '&:hover': { color: tokens.color.ink, background: tokens.color.surface2 },
          }}
        >
          {t('app.nav.opportunities')}
        </Box>
        <Box component="span" sx={{ color: tokens.color.muted2, fontSize: 11 }}>/</Box>
        <Box component="span" sx={{ color: tokens.color.ink, fontWeight: 600, padding: '4px 8px' }}>
          {t('app.nav.profile')}
        </Box>
      </Box>

      <Typography
        component="h1"
        sx={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.025em', m: '4px 0 6px' }}
      >
        {t('profile.title')}
      </Typography>
      <Typography sx={{ color: tokens.color.muted, fontSize: 14, mb: '36px' }}>
        {t('profile.lede')}
      </Typography>

      <Box component="form" onSubmit={handleSubmitProfile}>
      <Section
        eyebrow={t('profile.section.account.eyebrow')}
        title={t('profile.section.account.title')}
        sub={t('profile.section.account.sub')}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: '14px 16px',
            mb: '22px',
          }}
        >
          <ProfileField
            label={t('profile.field.firstName')}
            value={firstName}
            onChange={setFirstName}
          />
          <ProfileField
            label={t('profile.field.lastName')}
            value={lastName}
            onChange={setLastName}
          />
          <ProfileField
            label={t('profile.field.phone')}
            value={phone}
            onChange={setPhone}
            placeholder="+54 11 1234 5678"
          />
          <ProfileSelect
            label={t('profile.field.language')}
            value={language}
            onChange={setLanguage}
            options={[
              { value: 'es', label: 'Español' },
              { value: 'en', label: 'English' },
              { value: 'pt-BR', label: 'Português (BR)' },
            ]}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 14px',
            background: tokens.color.surface2,
            border: `1px solid ${tokens.color.lineSoft}`,
            borderRadius: '9px',
            fontFamily: tokens.font.mono,
            fontSize: 12.5,
            color: tokens.color.muted,
            mb: '22px',
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
            sx={{ width: 14, height: 14, color: tokens.color.muted2, flexShrink: 0 }}
            aria-hidden
          >
            <rect x="3" y="7" width="10" height="6.5" rx="1.5" />
            <path d="M5 7V5a3 3 0 016 0v2" />
          </Box>
          <Box component="span">
            {t('profile.signedInAs')}{' '}
            <Box component="strong" sx={{ color: tokens.color.ink2, fontWeight: 500 }}>
              {email}
            </Box>
          </Box>
          {workspaceName && (
            <Box
              component="span"
              sx={{
                ml: 'auto',
                fontSize: 10,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: tokens.color.muted2,
              }}
            >
              SSO · {workspaceName}
            </Box>
          )}
        </Box>

        {profileError && (
          <Box
            role="alert"
            sx={{
              mb: '14px',
              padding: '10px 14px',
              background: '#fde8e8',
              border: '1px solid #f5c6c6',
              borderRadius: '9px',
              color: '#7a1f1f',
              fontSize: 13,
            }}
          >
            {profileError}
          </Box>
        )}
        {profileSavedAt && !profileError && (
          <Box
            role="status"
            sx={{
              mb: '14px',
              padding: '10px 14px',
              background: '#e6f4ea',
              border: '1px solid #b6dfc0',
              borderRadius: '9px',
              color: '#1f6133',
              fontSize: 13,
            }}
          >
            {t('profile.actions.saved')}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Box
            component="button"
            type="submit"
            disabled={updateProfile.isPending}
            data-testid="profile-save"
            sx={{
              height: 38,
              padding: '0 18px',
              borderRadius: '9px',
              background: tokens.color.accent,
              color: tokens.color.accentInk,
              border: `1px solid ${tokens.color.accent}`,
              fontFamily: 'inherit',
              fontSize: 13.5,
              fontWeight: 600,
              cursor: updateProfile.isPending ? 'wait' : 'pointer',
              opacity: updateProfile.isPending ? 0.6 : 1,
            }}
          >
            {updateProfile.isPending ? t('profile.actions.saving') : t('profile.actions.save')}
          </Box>
        </Box>
      </Section>

      <Section
        eyebrow={t('profile.section.notifications.eyebrow')}
        title={t('profile.section.notifications.title')}
        sub={t('profile.section.notifications.sub')}
      >
        <NotificationRow
          title={t('profile.notif.newShortlist.title')}
          help={t('profile.notif.newShortlist.help')}
          checked={notifyNewShortlist}
          onChange={setNotifyNewShortlist}
        />
        <NotificationRow
          title={t('profile.notif.talentUpdate.title')}
          help={t('profile.notif.talentUpdate.help')}
          checked={notifyTalentUpdate}
          onChange={setNotifyTalentUpdate}
        />
        <NotificationRow
          title={t('profile.notif.weeklyDigest.title')}
          help={t('profile.notif.weeklyDigest.help')}
          checked={notifyWeeklyDigest}
          onChange={setNotifyWeeklyDigest}
        />

        <Box
          sx={{
            mt: '14px',
            borderTop: `1px dashed ${tokens.color.line}`,
            pt: '16px',
          }}
        >
          <Box
            sx={{
              fontFamily: tokens.font.mono,
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              color: tokens.color.muted2,
              mb: '10px',
            }}
          >
            {t('profile.following.title')}
          </Box>
          <Box sx={{ fontSize: 13, color: tokens.color.muted, padding: '8px 0' }}>
            {t('profile.following.empty')}{' '}
            <Box
              component={RouterLink}
              to="/opportunities"
              sx={{
                color: tokens.color.accent,
                textDecoration: 'none',
                fontWeight: 500,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {t('profile.following.browseLink')} →
            </Box>
          </Box>
        </Box>
      </Section>
      </Box>

      {/* SW-139: cambio de password — endpoint separado, form aislado por security
          (current + new + confirm) y para que el click "Cambiar contraseña" no se
          confunda con "Guardar perfil". */}
      <PasswordChangeSection />
    </Box>
  );
}

/**
 * SW-139: form de cambio de password aislado del Profile form. Endpoint distinto
 * (POST /me/password) con rate limit per-tenant + audit log dedicado.
 */
function PasswordChangeSection() {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const change = useMutation({
    mutationFn: async (body: ChangePasswordRequest) =>
      api.post<void>('/api/client/auth/me/password', body),
    onSuccess: () => {
      setSavedAt(Date.now());
      setError(null);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (err: unknown) => {
      const status = err instanceof ApiError ? err.status : 0;
      const body =
        err instanceof ApiError && err.body && typeof err.body === 'object'
          ? (err.body as { error?: string })
          : null;
      if (status === 400 && body?.error === 'weak_password') {
        setError(t('profile.password.errors.weak'));
      } else if (status === 400 && body?.error === 'invalid_current_password') {
        setError(t('profile.password.errors.invalidCurrent'));
      } else if (status === 400 && body?.error === 'no_password_set') {
        setError(t('profile.password.errors.noPassword'));
      } else {
        setError(t('profile.password.errors.generic'));
      }
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSavedAt(null);
    if (newPassword !== confirmPassword) {
      setError(t('profile.password.errors.mismatch'));
      return;
    }
    change.mutate({ currentPassword, newPassword });
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Section
        eyebrow={t('profile.section.password.eyebrow')}
        title={t('profile.section.password.title')}
        sub={t('profile.section.password.sub')}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '14px',
            mb: '18px',
          }}
        >
          <ProfileField
            label={t('profile.password.current')}
            value={currentPassword}
            onChange={setCurrentPassword}
            type="password"
            autoComplete="current-password"
          />
          <ProfileField
            label={t('profile.password.new')}
            value={newPassword}
            onChange={setNewPassword}
            type="password"
            autoComplete="new-password"
            help={t('profile.password.help')}
          />
          <ProfileField
            label={t('profile.password.confirm')}
            value={confirmPassword}
            onChange={setConfirmPassword}
            type="password"
            autoComplete="new-password"
          />
        </Box>

        {error && (
          <Box
            role="alert"
            sx={{
              mb: '14px',
              padding: '10px 14px',
              background: '#fde8e8',
              border: '1px solid #f5c6c6',
              borderRadius: '9px',
              color: '#7a1f1f',
              fontSize: 13,
            }}
          >
            {error}
          </Box>
        )}
        {savedAt && !error && (
          <Box
            role="status"
            sx={{
              mb: '14px',
              padding: '10px 14px',
              background: '#e6f4ea',
              border: '1px solid #b6dfc0',
              borderRadius: '9px',
              color: '#1f6133',
              fontSize: 13,
            }}
          >
            {t('profile.password.saved')}
          </Box>
        )}

        <Box
          component="button"
          type="submit"
          disabled={change.isPending || !currentPassword || !newPassword || !confirmPassword}
          data-testid="profile-password-submit"
          sx={{
            height: 38,
            padding: '0 18px',
            borderRadius: '9px',
            background: tokens.color.accent,
            color: tokens.color.accentInk,
            border: `1px solid ${tokens.color.accent}`,
            fontFamily: 'inherit',
            fontSize: 13.5,
            fontWeight: 600,
            cursor: change.isPending ? 'wait' : 'pointer',
            opacity: change.isPending || !currentPassword || !newPassword || !confirmPassword ? 0.5 : 1,
          }}
        >
          {change.isPending ? t('profile.password.changing') : t('profile.password.change')}
        </Box>
      </Section>
    </Box>
  );
}

/**
 * SW-139: una fila de notification toggle. Title + help + Toggle controlled.
 * Reusada 3 veces (NewShortlist / TalentUpdate / WeeklyDigest).
 */
function NotificationRow({
  title,
  help,
  checked,
  onChange,
}: {
  title: string;
  help: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: '18px',
        alignItems: 'start',
        py: '10px',
        borderBottom: `1px dashed ${tokens.color.line}`,
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Box component="span" sx={{ fontSize: 14, fontWeight: 600, color: tokens.color.ink }}>
          {title}
        </Box>
        <Box component="span" sx={{ fontSize: 12.5, color: tokens.color.muted, lineHeight: 1.5 }}>
          {help}
        </Box>
      </Box>
      <Toggle checked={checked} onChange={onChange} />
    </Box>
  );
}

function Section({
  eyebrow,
  title,
  sub,
  children,
}: {
  eyebrow: string;
  title: string;
  sub: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      component="section"
      sx={{
        background: tokens.color.surface,
        border: `1px solid ${tokens.color.line}`,
        borderRadius: '14px',
        padding: '26px 28px',
        mb: '18px',
        boxShadow: '0 1px 0 rgba(15,15,15,.04)',
      }}
    >
      <Box
        sx={{
          fontFamily: tokens.font.mono,
          fontSize: 10,
          fontWeight: 500,
          letterSpacing: '.14em',
          textTransform: 'uppercase',
          color: tokens.color.muted2,
          mb: '6px',
        }}
      >
        {eyebrow}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px', mb: '4px' }}>
        <Typography
          component="h2"
          sx={{ fontSize: 17, fontWeight: 700, letterSpacing: '-.01em', color: tokens.color.ink }}
        >
          {title}
        </Typography>
      </Box>
      <Typography sx={{ color: tokens.color.muted, fontSize: 13.5, mb: '22px' }}>{sub}</Typography>
      {children}
    </Box>
  );
}

function ProfileField({
  label,
  value,
  onChange,
  disabled,
  placeholder,
  type = 'text',
  autoComplete,
  help,
}: {
  label: string;
  value: string;
  onChange?: (next: string) => void;
  disabled?: boolean;
  placeholder?: string;
  type?: 'text' | 'password' | 'email' | 'tel';
  autoComplete?: string;
  help?: string;
}) {
  // SW-146: id/htmlFor para asociar label ↔ input (a11y + screen readers + tests).
  // useId genera una clave estable cross-render. Antes el label era huérfano y
  // los screen readers no anunciaban el campo al focar.
  const fieldId = useId();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <Box
        component="label"
        htmlFor={fieldId}
        sx={{
          fontFamily: tokens.font.mono,
          fontSize: 10.5,
          fontWeight: 500,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          color: tokens.color.muted,
        }}
      >
        {label}
      </Box>
      <Box
        component="input"
        id={fieldId}
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        readOnly={!onChange}
        disabled={disabled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange?.(e.target.value)}
        sx={{
          height: 42,
          padding: '0 14px',
          background: tokens.color.surface,
          border: `1px solid ${tokens.color.line}`,
          borderRadius: '9px',
          fontFamily: 'inherit',
          fontSize: 14,
          fontWeight: 500,
          color: tokens.color.ink,
          opacity: disabled ? 0.7 : 1,
          cursor: disabled ? 'not-allowed' : 'text',
          outline: 'none',
          '&:focus': {
            borderColor: tokens.color.accent,
            boxShadow: `0 0 0 3px ${tokens.color.accent}22`,
          },
        }}
      />
      {help && (
        <Box component="span" sx={{ fontSize: 11.5, color: tokens.color.muted, lineHeight: 1.45 }}>
          {help}
        </Box>
      )}
    </Box>
  );
}

function ProfileSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <Box
        component="label"
        sx={{
          fontFamily: tokens.font.mono,
          fontSize: 10.5,
          fontWeight: 500,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          color: tokens.color.muted,
        }}
      >
        {label}
      </Box>
      <Box
        component="select"
        value={value}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
        sx={{
          height: 42,
          padding: '0 14px',
          background: tokens.color.surface,
          border: `1px solid ${tokens.color.line}`,
          borderRadius: '9px',
          fontFamily: 'inherit',
          fontSize: 14,
          fontWeight: 500,
          color: tokens.color.ink,
          outline: 'none',
          '&:focus': {
            borderColor: tokens.color.accent,
            boxShadow: `0 0 0 3px ${tokens.color.accent}22`,
          },
        }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Box>
    </Box>
  );
}

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked?: boolean;
  onChange?: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <Box
      component="label"
      sx={{
        position: 'relative',
        display: 'inline-block',
        width: 40,
        height: 22,
        flexShrink: 0,
        opacity: disabled ? 0.4 : 1,
      }}
    >
      <Box
        component="input"
        type="checkbox"
        checked={checked ?? false}
        disabled={disabled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange?.(e.target.checked)}
        sx={{ opacity: 0, width: 0, height: 0 }}
      />
      <Box
        component="span"
        sx={{
          position: 'absolute',
          inset: 0,
          background: checked ? tokens.color.accent : tokens.color.line,
          borderRadius: '999px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'background .18s',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 2,
            left: 2,
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: '#fff',
            boxShadow: '0 1px 2px rgba(0,0,0,.2), 0 1px 3px rgba(0,0,0,.06)',
            transition: 'transform .18s',
            transform: checked ? 'translateX(18px)' : 'translateX(0)',
          },
        }}
      />
    </Box>
  );
}
